import * as yup from 'yup'
import { CENTS_PATTERN, RATE_PATTERN } from 'src/constants/payment/constants'
import { PaymentPeriodType } from 'src/constants/payment/enums'
import type { VendorFeeFormRow } from './VendorFeesForm.interface'

const RATE_MESSAGE = 'Enter 0 to 99.99 with at most two decimal places'
const CENTS_MESSAGE = 'Enter a whole number of cents'
const FEE_OR_RATE_MESSAGE = 'Enter a fee or a rate above zero'

function isPositive(value: string | undefined): boolean {
  if (value === undefined) {
    return false
  }

  const parsed = Number(value)

  if (!Number.isFinite(parsed)) {
    return false
  }

  return parsed > 0
}

/**
 * Attached to `fee` AND `rate` rather than to the row object. A `.test()` on the object schema
 * produces the error path `rows[0]`, which Formik's `setIn` turns into a STRING at
 * `errors.rows[0]`; every input then reads `getIn(errors, 'rows[0].fee')` off that string and
 * gets `undefined`, so the submit is blocked with nothing shown on screen. Field-scoped rules
 * land on `rows[0].fee` / `rows[0].rate`, which the inputs do render.
 */
function hasFeeOrRate(row: VendorFeeFormRow | undefined): boolean {
  if (row === undefined) {
    return true
  }

  return isPositive(row.fee) || isPositive(row.rate)
}

/**
 * `applicableDestinations` and `contractorType` have NO minimum. The DTO requires the keys to be
 * present but accepts empty arrays, and live rows genuinely carry empty ones — a minimum-one
 * rule would stop an admin saving a legacy row they never touched.
 *
 * Two rules mirror server-side throws the admin would otherwise meet as a domain exception:
 * the fee-or-rate rule (which also replaces Retool's silent row-dropping filter) and the two
 * `feeCapCents` preconditions.
 */
const vendorFeeRowSchema = yup.object({
  title: yup.string().trim().required('Enter a title'),
  type: yup
    .string()
    .required('Select a type')
    .test('known-or-preserved', 'Select a type', (value) => {
      return typeof value === 'string' && value !== ''
    }),
  // Blank is valid and means zero — see the note in the payout-settings validator. The
  // fee-or-rate rule below is what still stops a row from being saved with neither.
  rate: yup
    .string()
    .matches(RATE_PATTERN, { message: RATE_MESSAGE, excludeEmptyString: true })
    .test('fee-or-rate', FEE_OR_RATE_MESSAGE, function () {
      return hasFeeOrRate(this.parent as VendorFeeFormRow)
    }),
  fee: yup
    .string()
    .matches(CENTS_PATTERN, { message: CENTS_MESSAGE, excludeEmptyString: true })
    .test('fee-or-rate', FEE_OR_RATE_MESSAGE, function () {
      return hasFeeOrRate(this.parent as VendorFeeFormRow)
    }),
  feeCapCents: yup
    .string()
    .matches(CENTS_PATTERN, { message: CENTS_MESSAGE, excludeEmptyString: true })
    // `updatePartnerBuyer` throws PartnerInvoiceBreakdownMaxFeeValidationError when a truthy
    // feeCapCents has no rate, and PartnerInvoiceBreakdownMaxFeePeriodValidationError when the
    // period is not per_payment. Zero is falsy server-side and skips both checks.
    .test('max-fee-needs-rate', 'A max fee needs a rate above zero', function (value) {
      if (!isPositive(value)) {
        return true
      }

      return isPositive((this.parent as VendorFeeFormRow).rate)
    })
    .test('max-fee-per-payment-only', 'A max fee only applies per payment', function (value) {
      if (!isPositive(value)) {
        return true
      }

      return (this.parent as VendorFeeFormRow).type === PaymentPeriodType.PerPayment
    }),
  applicableDestinations: yup.array().of(yup.string().required()),
  contractorType: yup.array().of(yup.string().required()),
})

export const vendorFeesValidator = yup.object({
  rows: yup.array().of(vendorFeeRowSchema),
})
